<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useDonorPlan } from '@/composables/useDonorPlan'
import { useClaimTaxBack } from '@/composables/useClaimTaxBack'
import { useDashboardNotifications } from '@/composables/useDashboardNotifications'
import { useDashboardNavFeedback } from '@/composables/useDashboardNavFeedback'
import { useUpgradeModal } from '@/composables/useUpgradeModal'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import NotificationIndicator from '@/components/dashboard/ui/NotificationIndicator.vue'
import PaidPlanBadge from '@/components/dashboard/ui/PaidPlanBadge.vue'
import MockPersonaSwitcher from '@/components/dashboard/dev/MockPersonaSwitcher.vue'

defineProps<{
  mobileOpen?: boolean
}>()

const route = useRoute()
const store = useStore()
const { t } = useI18n()
const { canViewGivingRecord, isPaid, isPlanKnown } = useDonorPlan()
const { isClaimAvailable, showNewBadge } = useClaimTaxBack()
const { actionCount } = useDashboardNotifications()
const { pendingRouteName, beginNavFeedback } = useDashboardNavFeedback()
const { openUpgradeModal } = useUpgradeModal()

const isAuth = computed(() => store.state.auth.authenticated)

const displayName = computed(() => {
  const details = store.state.donor?.userDetails?.details
  if (details?.firstname || details?.lastname) {
    return [details.firstname, details.lastname].filter(Boolean).join(' ')
  }
  return store.state.donor?.userDetails?.emailAddress ?? ''
})

const email = computed(
  () => store.state.donor?.userDetails?.emailAddress as string | undefined
)

const initials = computed(() => {
  const details = store.state.donor?.userDetails?.details
  if (details?.firstname && details?.lastname) {
    return `${details.firstname[0]}${details.lastname[0]}`.toUpperCase()
  }
  const addr = email.value
  if (addr) return addr.slice(0, 2).toUpperCase()
  return '?'
})

type DashboardNavItem = {
  name:
    | 'Dashboard'
    | 'Statements'
    | 'Donations'
    | 'ClaimTaxBack'
    | 'Account'
    | 'Support'
  routeName:
    | 'Dashboard'
    | 'Statements'
    | 'Donations'
    | 'ClaimTaxBack'
    | 'Account'
    | 'Support'
  nav: string
  icon:
    | 'dashboard'
    | 'statements'
    | 'donations'
    | 'claim'
    | 'settings'
    | 'support'
  showLock?: boolean
  /** "New" marker until the donor first opens the page (free and paid alike). */
  showNew?: boolean
}

const navItems = computed<DashboardNavItem[]>(() => [
  {
    name: 'Dashboard',
    routeName: 'Dashboard',
    nav: 'dashboard',
    icon: 'dashboard',
  },
  {
    name: 'Donations',
    routeName: 'Donations',
    nav: 'donations',
    icon: 'donations',
    showLock: true,
  },
  // Unlocked on free accounts: the claim checker itself is free to use.
  ...(isClaimAvailable.value
    ? [
        {
          name: 'ClaimTaxBack',
          routeName: 'ClaimTaxBack',
          nav: 'claim-tax-back',
          icon: 'claim',
          showNew: showNewBadge.value,
        } as DashboardNavItem,
      ]
    : []),
  {
    name: 'Statements',
    routeName: 'Statements',
    nav: 'statements',
    icon: 'statements',
  },
  { name: 'Account', routeName: 'Account', nav: 'account', icon: 'settings' },
  { name: 'Support', routeName: 'Support', nav: 'support', icon: 'support' },
])

function isNavActive(nav: string): boolean {
  return (route.meta.dashboardNav as string | undefined) === nav
}

function isNavPending(routeName: string): boolean {
  return pendingRouteName.value === routeName
}

function onNavSelect(routeName: string) {
  if (route.name === routeName) return
  beginNavFeedback(routeName)
}

const logout = () => {
  store.dispatch('auth0Logout')
}
</script>

<template>
  <aside
    id="dashboard-sidebar"
    class="sa-sidebar"
    :class="{ 'sa-sidebar--open': mobileOpen }"
  >
    <router-link
      class="sa-sidebar-logo sa-sidebar-logo-link"
      :to="{ name: 'Dashboard' }"
    >
      <img
        class="sa-sidebar-logo-img"
        src="@/assets/images/main/logo.svg"
        alt="Swiftaid"
        width="120"
        height="30"
      />
    </router-link>

    <nav class="sa-sidebar-nav" aria-label="Main navigation">
      <router-link
        v-for="item in navItems"
        :key="item.routeName"
        class="sa-nav-item"
        :class="{
          'sa-nav-item--active': isNavActive(item.nav),
          'sa-nav-item--pending': isNavPending(item.routeName),
        }"
        :to="{ name: item.routeName }"
        :data-tour-nav="item.nav"
        :aria-current="isNavActive(item.nav) ? 'page' : undefined"
        :aria-busy="isNavPending(item.routeName) ? true : undefined"
        @click="onNavSelect(item.routeName)"
        :aria-label="
          item.name === 'Dashboard' && actionCount > 0
            ? t('Dashboard.Shell.Nav.DashboardWithNotifications', {
                count: actionCount,
              })
            : undefined
        "
      >
        <DashboardNavIcon :name="item.icon" />
        <span class="sa-nav-item__label">
          {{ t(`Dashboard.Shell.Nav.${item.name}`) }}
        </span>
        <NotificationIndicator
          v-if="item.name === 'Dashboard' && actionCount > 0"
          variant="nav"
          :count="actionCount"
        />
        <span
          v-if="item.showNew"
          class="sa-nav-item__new"
          :aria-label="t('Dashboard.Shell.Nav.NewFeatureAria')"
        >
          {{ t('Dashboard.Shell.Nav.NewBadge') }}
        </span>
        <svg
          v-if="item.showLock && isPlanKnown && !canViewGivingRecord"
          class="sa-nav-item__lock"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clip-rule="evenodd"
          />
        </svg>
      </router-link>

      <div v-if="isPlanKnown && !isPaid" class="sa-sidebar-upgrade">
        <button
          type="button"
          class="sa-sidebar-upgrade__btn"
          data-tour-nav="upgrade"
          @click="openUpgradeModal"
        >
          {{ t('Dashboard.Home.UpgradeCta') }}
          <PaidPlanBadge />
        </button>
      </div>

      <button
        v-if="isAuth"
        type="button"
        class="sa-nav-item sa-nav-item--logout"
        @click="logout"
      >
        <DashboardNavIcon name="logout" />
        <span>{{ t('Dashboard.Shell.Nav.Logout') }}</span>
      </button>
    </nav>

    <div class="sa-sidebar-footer">
      <MockPersonaSwitcher />
      <router-link
        class="sa-user-chip"
        :to="{ name: 'Account' }"
        @click="onNavSelect('Account')"
      >
        <div class="sa-user-avatar" aria-hidden="true">{{ initials }}</div>
        <div class="sa-user-chip__text">
          <div class="sa-user-name">{{ displayName }}</div>
          <div v-if="email" class="sa-user-email">{{ email }}</div>
        </div>
      </router-link>
    </div>
  </aside>
</template>
